import React from "react";

import { useBotbRAFContext } from "../context";

export const ReferAFriendStatistics = (): React.ReactNode => {
  const { statistics, rafStatus, calculatePercentage } = useBotbRAFContext();
  const getFirstIncompleteTask = (statistics: any[]): any | null => {
    if (!Array.isArray(statistics) || statistics.length === 0) return null;
    const firstIncompleteTask = statistics.find(item => item?.tasks?.[0]?.is_completed === false);
    return firstIncompleteTask || statistics[0];
  };

  const statisticsText = {
    yourProgress: "Your progress",
    earned: "Earned",
    referrals: "Referrals",
    referral: "Referral",
    next: `Next milestone: ${getFirstIncompleteTask(statistics)?.name}`,
    nextCompleted: "🎉 You’ve completed the missions! 🎉",
    moreReferrals: `more referral${getFirstIncompleteTask(statistics)?.tasks[0].execution_count_expected - getFirstIncompleteTask(statistics)?.tasks[0].execution_count_actual > 1 ? "s" : ""}`,
    sendInviteBtnNoFriends: "Invite friends",
    earnedText: {
      gameCredit: "Game credit",
      raffle: "+ Raffle",
    },
    currency: "£",
  };

  const calculateRewardSum = (items: any[]): number => {
    if (!Array.isArray(items)) return 0;

    // Get all items containing `tasks[0].is_completed`
    const filteredItems = items.filter(item => item.tasks?.[0]?.is_completed);
    // Filter all items that contain a `reward`
    const itemsWithReward = filteredItems.filter(item => item.reward);
    // Process the `reward` values
    const sum = itemsWithReward.reduce((acc, item) => {
      const firstPart = item.reward.split(" ")[0];
      const numericValue = parseFloat(firstPart.replace(/^£|#/g, ""));
      return acc + (isNaN(numericValue) ? 0 : numericValue);
    }, 0);

    // Return the sum
    return sum;
  };

  const imageMap = {
    39792: "/assets/images/refer-a-friend/01-first-friend.png",
    39793: "/assets/images/refer-a-friend/02-triple-treath.png",
    39806: "/assets/images/refer-a-friend/03-high-five.png",
    39807: "/assets/images/refer-a-friend/04-perfect-ten.png",
    39809: "/assets/images/refer-a-friend/05-elite-circle.png",
    39810: "/assets/images/refer-a-friend/06-champion.png",
    39811: "/assets/images/refer-a-friend/07-legend-status.png",
  };

  return (
    <>
      <div className={"relative font-jost"}>
        {/*top box*/}
        <div
          className={
            "absolute top-0 inset-x-4 lg:inset-x-10 w-[calc(100%-2rem)] lg:w-[calc(100%-80px)] bg-white z-10 rounded-lg border border-black/8 shadow-[0px_4px_8px_0px_rgba(0,0,0,0.12)] min-h-[125px]"
          }>
          <div className={"px-2.5 pt-3 pb-4"}>
            {/*section up*/}
            <div className={"mb-3 flex items-center justify-between"}>
              <div className={""}>
                <div className={"text-gray-5 text-xs lg:text-sm leading-4 lg:leading-5 font-normal mb-1"}>
                  {statisticsText.yourProgress}
                </div>
                <div className={"text-secondary text-sm lg:text-base font-normal leading-5 lg:leading-5.5"}>
                  <span
                    className={`font-medium ${rafStatus.referredFriends === rafStatus.maximumInvites ? "text-green-1" : "text-primary"}`}>
                    {rafStatus.referredFriends}
                  </span>
                  /<span>{rafStatus.maximumInvites}</span> {statisticsText.referrals}
                </div>
              </div>
              <div>
                <div className={"text-gray-5 text-xs lg:text-sm leading-4 lg:leading-5 font-normal mb-1 text-right"}>
                  {statisticsText.earned}
                </div>
                <div className={"text-secondary text-sm lg:text-base font-medium leading-5 lg:leading-5.5 text-right"}>
                  {statisticsText.currency}
                  {calculateRewardSum(statistics)} {statisticsText.earnedText.gameCredit}{" "}
                  {statistics.some(
                    item => item.tasks?.[0]?.is_completed === true && item.reward?.toLowerCase().includes("raffle")
                  ) && statisticsText.earnedText.raffle}
                </div>
              </div>
            </div>

            {/*section divider*/}
            <div className={"w-full h-0 border-t border-black/8 mb-2"}></div>

            {/*section down*/}
            <div>
              <div className={"flex items-center justify-between mb-2"}>
                <div className={"text-secondary text-sm lg:text-base font-medium leading-5 lg:leading-5.5"}>
                  <span>
                    {rafStatus.referredFriends < rafStatus.maximumInvites ? (
                      <>{statisticsText.next}</>
                    ) : (
                      <>{statisticsText.nextCompleted}</>
                    )}
                  </span>
                </div>
                {rafStatus.referredFriends < rafStatus.maximumInvites && (
                  <div className={"text-gray-5 text-xs lg:text-sm leading-4 lg:leading-5 font-normal text-right"}>
                    <span>
                      {getFirstIncompleteTask(statistics)?.tasks[0].execution_count_expected -
                        getFirstIncompleteTask(statistics)?.tasks[0].execution_count_actual}
                    </span>{" "}
                    {statisticsText.moreReferrals}
                  </div>
                )}
              </div>
              <div className={"h-2 rounded-full w-full bg-gray-12"}>
                <div
                  className={`h-full rounded-full ${rafStatus.referredFriends === rafStatus.maximumInvites ? "bg-green-1" : "bg-primary"}`}
                  style={{
                    width: calculatePercentage(
                      rafStatus.referredFriends > rafStatus.maximumInvites
                        ? rafStatus.maximumInvites
                        : rafStatus.referredFriends,
                      rafStatus.maximumInvites
                    ),
                  }}></div>
              </div>
            </div>
          </div>
        </div>

        {/*scrolled content - space above*/}
        <div
          className={
            "pt-[150px] px-4 overflow-y-auto max-h-[calc(100vh-160px)] lg:max-h-[400px] lg:pl-14 lg:pr-[72px] relative custom-scrollbar"
          }>
          <div className={"pb-12"}>
            {statistics.map((item, index) => (
              <>
                {!item.tasks[0].is_completed ? (
                  // gray template
                  <div key={index} className={"mb-4 flex items-center w-full gap-2"}>
                    <div className={"relative flex-center min-w-10 lg:min-w-12"}>
                      <div className={"size-10 lg:size-12 rounded-full flex-center bg-gray-4 border border-gray-4"}>
                        <img
                          src={imageMap[item.id]}
                          alt={item.name}
                          className={"size-7 lg:size-8 grayscale opacity-30"}
                        />
                      </div>
                      {/*vertical divider*/}
                      {index + 1 !== statistics.length && (
                        <div className={"h-12 w-0.5 bg-gray-4 absolute top-full left-1/2 -translate-x-1/2"}></div>
                      )}
                    </div>

                    <div
                      className={
                        "flex items-center justify-between w-full px-4 py-2 rounded-lg bg-gray-4 border border-gray-4 min-h-[72px] lg:min-h-20"
                      }>
                      <div className={""}>
                        <div className={"text-primary uppercase text-[11px] lg:text-sm font-medium leading-4"}>
                          {item.name}
                        </div>
                        <div className={"text-secondary font-base lg:text-lg font-bold leading-5.5 lg:leading-6"}>
                          {item.reward}
                        </div>
                        <div className={"text-gray-5 text-[11px] lg:text-sm font-medium leading-3.5 lg:leading-4"}>
                          {item.tasks[0].execution_count_expected} {statisticsText.referral}
                        </div>
                      </div>

                      <div className={"ml-auto"}>
                        <img
                          src={"/assets/images/refer-a-friend/lock.svg"}
                          alt={"lock icon"}
                          className={"size-6 lg:size-8"}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  // green template
                  <div key={index} className={"mb-4 flex items-center w-full gap-2"}>
                    <div className={"relative flex-center min-w-10 lg:min-w-12"}>
                      <div className={"size-10 lg:size-12 rounded-full flex-center bg-green-10 border border-green-3"}>
                        <img src={imageMap[item.id]} alt={item.title} className={"size-7 lg:size-8"} />
                      </div>
                      {/*vertical divider*/}
                      {index + 1 !== statistics.length && (
                        <div className={"h-12 w-0.5 bg-green-10 absolute top-full left-1/2 -translate-x-1/2"}></div>
                      )}
                    </div>

                    <div
                      className={
                        "flex items-center justify-between w-full px-4 py-2 rounded-lg bg-green-10 border border-green-3 min-h-[72px] lg:min-h-20"
                      }>
                      <div className={""}>
                        <div className={"text-green-1 uppercase text-[11px] lg:text-sm font-medium leading-4"}>
                          {item.name}
                        </div>
                        <div className={"text-secondary font-base lg:text-lg font-bold leading-5.5 lg:leading-6"}>
                          {item.reward}
                        </div>
                        <div className={"text-gray-5 text-[11px] lg:text-sm font-medium leading-3.5 lg:leading-4"}>
                          {item.tasks[0].execution_count_expected} {statisticsText.referral}
                        </div>
                      </div>

                      <div className={"ml-auto"}>
                        <img
                          src={"/assets/images/refer-a-friend/green-check.svg"}
                          alt={"checked icon"}
                          className={"size-6 lg:size-8"}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
